import React from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'

import { CurrencyBtcIcon, InvestingIcon, SavingsIcon } from '@arcade/web/icons/react'
import { Hug, ListOrdered, maxWidths, RadioInputCard, spacing, Stack, Text } from '@cash-design-system/react'
import { RoundUpOnboardingBlocker as RoundUpOnboardingBlockerType } from '@cash-web/protos/squareup/franklin/blockers/invest_automator.pb'
import { useMutationSubmitForm } from '@cash-web/shared-data-access-api/react-query'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'

import { BaseBlocker, BaseBlockerProps } from '../../BaseBlocker/BaseBlocker'
import { BlockerActionHeader } from '../../BlockerActionHeader'
import { RoundUpInvestmentEntitySelection } from '../BlockerInvestmentEntitySelection/RoundUpInvestmentEntitySelection'

const FullWidthImage = styled.img`
  width: 100%;
`

const OnboardingIntroScreen = ({
  ...props
}: RoundUpOnboardingBlockerType['onboarding_intro_screen'] & { continue: () => void }) => {
  const theme = useTheme()
  const form = useForm()
  const textTokenizer = useTextTokenizer()

  return (
    <div>
      <FullWidthImage
        src={
          (theme.darkMode ? props.illustration?.dark_url : props.illustration?.light_url) ??
          props.illustration?.light_url
        }
        alt=""
      />
      <BaseBlocker mainText={props.title} onSubmit={props.continue} form={form}>
        <Stack space={spacing.l}>
          <Text typography="body">{props.description}</Text>
          <ListOrdered
            items={
              props.instructions?.map(instruction => (
                <Stack key={instruction.title} space="0">
                  <Text typography="labelMedium">{instruction.title}</Text>
                  <Text>{instruction.description}</Text>
                </Stack>
              )) ?? []
            }
          />
          <Text typography="bodySmall" textAlign="center">
            {textTokenizer(props.equity_fineprint_markdown)}
          </Text>
        </Stack>
      </BaseBlocker>
    </div>
  )
}

const DestinationSelectorScreen = ({
  ...props
}: RoundUpOnboardingBlockerType['destination_selector_screen'] & {
  transitionToInvesting: () => void
  transitionToBitcoin: () => void
  transitionToSavings: () => void
  actionHeader: BaseBlockerProps['actionHeader']
}) => {
  const form = useForm()
  const theme = useTheme()

  return (
    <BaseBlocker
      mainText={props.title}
      secondaryText={props.description}
      onSubmit={() => {
        return
      }}
      form={form}
      buttons={[]}
      actionHeader={props.actionHeader}
    >
      <Stack space={spacing.l}>
        <RadioInputCard
          cellProps={{
            asset: {
              avatar: (
                <div style={{ color: theme.colors.investing }}>
                  <InvestingIcon />
                </div>
              ),
            },
          }}
          label={props['equity_button_label'] ?? ''}
          checked={false}
          onChange={props.transitionToInvesting}
        />
        <RadioInputCard
          cellProps={{
            asset: {
              avatar: (
                <div style={{ color: theme.colors.bitcoinBlue }}>
                  <CurrencyBtcIcon />
                </div>
              ),
            },
          }}
          label={props['bitcoin_button_label'] ?? ''}
          checked={false}
          onChange={props.transitionToBitcoin}
        />
        <RadioInputCard
          cellProps={{
            asset: {
              avatar: (
                <div style={{ color: theme.colors.primary }}>
                  <SavingsIcon />
                </div>
              ),
            },
          }}
          label={props['savings_button_label'] ?? ''}
          checked={false}
          onChange={props.transitionToSavings}
        />
      </Stack>
    </BaseBlocker>
  )
}

export const Investing = (props: {
  stock_selector_screen: RoundUpOnboardingBlockerType['stock_selector_screen']
  actionHeader: BaseBlockerProps['actionHeader']
}) => {
  return (
    <RoundUpInvestmentEntitySelection
      blocker={{ stock_selector_screen: props.stock_selector_screen }}
      actionHeader={props.actionHeader}
    />
  )
}

export const RoundUpOnboardingBlocker = ({ blocker }: { blocker: RoundUpOnboardingBlockerType }) => {
  const journey = useJourneyContext()
  const flow = useFlowContext()
  const { mutateAsync: mutateSubmitForm } = useMutationSubmitForm()

  const [blockerState, setBlockerState] = React.useState<'INTRO' | 'DESTINATION_SELECTOR' | 'INVESTING'>('INTRO')

  return (
    <Hug maxWidth={maxWidths.content} fullWidth={true}>
      {blockerState === 'INTRO' && (
        <OnboardingIntroScreen
          {...blocker.onboarding_intro_screen}
          continue={() => {
            setBlockerState('DESTINATION_SELECTOR')
          }}
        />
      )}
      {blockerState === 'DESTINATION_SELECTOR' && (
        <DestinationSelectorScreen
          {...blocker.destination_selector_screen}
          actionHeader={
            <BlockerActionHeader
              onBack={() => {
                setBlockerState('INTRO')
              }}
              onClose={() => {
                journey.cancelAll()
              }}
            />
          }
          transitionToInvesting={() => {
            setBlockerState('INVESTING')
          }}
          transitionToBitcoin={() => {
            flow.updateWithResponse({
              response: mutateSubmitForm({ action_id: 'PRODUCT_DESTINATION_SELECTION_BTC_ACTION_ID' }),
              queriesToInvalidate: [],
            })
          }}
          transitionToSavings={() => {
            flow.updateWithResponse({
              response: mutateSubmitForm({ action_id: 'PRODUCT_DESTINATION_SELECTION_SAVINGS_ACTION_ID' }),
              queriesToInvalidate: [],
            })
          }}
        />
      )}
      {blockerState === 'INVESTING' && (
        <Investing
          stock_selector_screen={blocker.stock_selector_screen}
          actionHeader={
            <BlockerActionHeader
              onBack={() => {
                setBlockerState('DESTINATION_SELECTOR')
              }}
              onClose={() => {
                journey.cancelAll()
              }}
            />
          }
        />
      )}
    </Hug>
  )
}
